import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Tabs,
  TabList,
  Tab,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Checkbox,
  Text,
  Box,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useLibraryTracksInfinite, useCustomContentInfinite } from '../hooks/useTrackPicker';
import { useAddTracks } from '../hooks/usePlaylists';
import { formatTrackDuration } from '../utils';

interface AddTracksModalProps {
  playlistId: string;
  isOpen: boolean;
  onClose: () => void;
  existingContentIds: Set<string>;
}

export function AddTracksModal({ playlistId, isOpen, onClose, existingContentIds }: AddTracksModalProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const addTracks = useAddTracks(playlistId);
  const source: 'library' | 'own' = tab === 0 ? 'library' : 'own';

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set());
      setSearchInput('');
      setSearch('');
      setTab(0);
    }
  }, [isOpen]);

  const lib = useLibraryTracksInfinite(search, isOpen && source === 'library');
  const own = useCustomContentInfinite(search, isOpen && source === 'own');

  const items = useMemo(() => {
    const raw =
      source === 'library'
        ? lib.data?.pages.flatMap((p) => p.items) ?? []
        : own.data?.pages.flatMap((p) => p.items) ?? [];
    return raw.map((it) => ({
      id: it.id,
      title: it.title,
      artist: it.artist,
      durationSeconds: it.durationSeconds,
    }));
  }, [source, lib.data, own.data]);

  const isLoading = source === 'library' ? lib.isLoading : own.isLoading;
  const hasNextPage = source === 'library' ? lib.hasNextPage : own.hasNextPage;
  const isFetchingNextPage = source === 'library' ? lib.isFetchingNextPage : own.isFetchingNextPage;
  const loadMore = () => (source === 'library' ? lib.fetchNextPage() : own.fetchNextPage());

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleAdd = async () => {
    if (selected.size === 0) return;
    await addTracks.mutateAsync([...selected]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>{t('playlists.addTracks')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs index={tab} onChange={setTab} colorScheme="brand" mb={3}>
            <TabList>
              <Tab>{t('playlists.sourceLibrary')}</Tab>
              <Tab>{t('playlists.sourceOwn')}</Tab>
            </TabList>
          </Tabs>

          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder={t('playlists.searchTracks')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>

          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner color="brand.500" />
            </Flex>
          ) : items.length === 0 ? (
            <Text color="gray.400" py={8} textAlign="center">
              {t('playlists.noTracksFound')}
            </Text>
          ) : (
            <VStack align="stretch" spacing={0}>
              {items.map((it) => {
                const already = existingContentIds.has(it.id);
                return (
                  <HStack
                    key={it.id}
                    py={2}
                    px={1}
                    spacing={3}
                    _hover={{ bg: 'gray.50' }}
                    borderRadius="md"
                    opacity={already ? 0.5 : 1}
                  >
                    <Checkbox
                      isChecked={selected.has(it.id) || already}
                      isDisabled={already}
                      onChange={() => toggle(it.id)}
                      colorScheme="brand"
                    />
                    <Box flex={1} minW={0}>
                      <Text fontWeight="500" noOfLines={1}>
                        {it.title}
                      </Text>
                      {it.artist && (
                        <Text fontSize="sm" color="gray.500" noOfLines={1}>
                          {it.artist}
                        </Text>
                      )}
                    </Box>
                    {already && (
                      <Text fontSize="xs" color="gray.400">
                        {t('playlists.alreadyAdded')}
                      </Text>
                    )}
                    <Text fontSize="sm" color="gray.400" flexShrink={0}>
                      {formatTrackDuration(it.durationSeconds)}
                    </Text>
                  </HStack>
                );
              })}
              {hasNextPage && (
                <Flex justify="center" mt={3}>
                  <Button size="sm" variant="ghost" onClick={loadMore} isLoading={isFetchingNextPage}>
                    {t('playlists.loadMore')}
                  </Button>
                </Flex>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleAdd}
            isLoading={addTracks.isPending}
            isDisabled={selected.size === 0}
          >
            {t('playlists.addSelected', { count: selected.size })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AddTracksModal;
